import React, { useEffect, useMemo, useRef } from 'react';
import { FrameRarity } from './cosmetic-visuals';
import {
  ParticleType,
  registerParticles,
  unregisterParticles,
  prefersReducedMotion,
} from './avatar-frame-particles';
import mythicAvatarFrame from '../../images/cosmetics/mythic-avatar-frame.png';
import legendaryAvatarFrame from '../../images/cosmetics/legendary-avatar-frame.png';
import epicAvatarFrame from '../../images/cosmetics/epic-avatar-frame.png';
import rareAvatarFrame from '../../images/cosmetics/rare-avatar-frame.png';
import commonAvatarFrame from '../../images/cosmetics/common-avatar-frame.png';

// Bậc dùng khung ẢNH (thay cho SVG vẽ tay).
//  - Thần Thoại (mythic): khung vàng-thiên hà (medals_2) + hạt xoay (AVAcab).
//  - Sử Thi (epic): khung tím-cánh-bạc — khung + quầng sáng, chưa gắn hạt xoay.
//  - Hiếm (rare): khung xanh dương-cánh-bạc — khung + quầng sáng.
//  - Cổ Điển (common): khung bạc/thép 4 gem + khói xám — khung + quầng sáng.
const FRAME_IMG: Partial<Record<FrameRarity, string>> = {
  common: commonAvatarFrame,
  rare: rareAvatarFrame,
  epic: epicAvatarFrame,
  legendary: legendaryAvatarFrame,
  mythic: mythicAvatarFrame,
};

// Tỉ lệ khung / avatar (đo từ chính ảnh khung). Mặc định 1.525 (AVAcab).
// Huyền Thoại avatar=58% khung → 1.724; Cổ Điển/Hiếm/Sử Thi (ava123) avatar=165/270 → 1.636.
const FRAME_MULT: Partial<Record<FrameRarity, number>> = {
  legendary: 1.724,
  common: 1.636,
  rare: 1.636,
  epic: 1.636,
};

// Bảng màu hạt bụi xoay quanh viền theo từng bậc (Thần Thoại=test-hieuung; Cổ Điển/Hiếm/Sử Thi=ava123).
const VORTEX_PALETTE: Partial<Record<FrameRarity, string[]>> = {
  common: ['#ffffff', '#b0c4de', '#e0ffff', '#87ceeb', '#dcdcdc'],
  rare: ['#00ffff', '#ffffff', '#1e90ff', '#00bfff', '#87cefa'],
  epic: ['#da70d6', '#ffffff', '#ba55d3', '#dda0dd', '#9370db'],
  mythic: ['#00ffff', '#ffffff', '#ffd700', '#da70d6', '#00ffaa', '#ab82ff'],
};

// Huyền Thoại: 4 hạt sáng nhẹ bay lên trong avatar (theo avalegend.html).
const FIREFLIES = [
  { l: 25, b: 10, d: 3.5, dl: 0.2, s: 3, c: '#ffea85' },
  { l: 50, b: 5, d: 4.0, dl: 1.2, s: 4, c: '#ffffff' },
  { l: 70, b: 15, d: 3.0, dl: 0.7, s: 2, c: '#ffea85' },
  { l: 40, b: 8, d: 4.5, dl: 2.0, s: 3, c: '#ffea85' },
];

interface Cfg {
  glow: string;
  particle: ParticleType;
  color: string;
  density: number;
  fastPulse?: boolean;
}

const CFG: Record<FrameRarity, Cfg> = {
  common: { glow: 'radial-gradient(circle,#94a3b8 0%,#334155 60%,transparent 100%)', particle: 'ember', color: '#94a3b8', density: 10 },
  rare: { glow: 'radial-gradient(circle,#38bdf8 0%,#1e40af 60%,transparent 100%)', particle: 'ember', color: '#38bdf8', density: 16 },
  epic: { glow: '#7e22ce', particle: 'ember', color: '#c084fc', density: 22 },
  legendary: {
    glow: 'radial-gradient(circle,#f59e0b 0%,#b45309 70%,transparent 100%)',
    particle: 'dragon', color: '#f59e0b', density: 30,
  },
  mythic: {
    // khung ảnh vàng-thiên hà xanh/tím → quầng sáng xanh dương→tím (theo AVAcab)
    glow: 'radial-gradient(circle,#38bdf8 0%,#7e22ce 60%,transparent 100%)',
    particle: 'void', color: '#38bdf8', density: 40, fastPulse: true,
  },
};

// SVG khung theo từng bậc (viewBox 0 0 200 200; avatar nằm giữa r≈50).
const FRAME: Record<FrameRarity, React.ReactNode> = {
  common: (
    <>
      <circle cx="100" cy="100" r="58" fill="none" stroke="url(#afSteel)" strokeWidth="8" />
      <circle cx="100" cy="100" r="63" fill="none" stroke="#000" strokeWidth="2" />
      <rect x="97" y="34" width="6" height="6" fill="#cbd5e1" rx="1" />
      <rect x="97" y="160" width="6" height="6" fill="#cbd5e1" rx="1" />
      <rect x="34" y="97" width="6" height="6" fill="#cbd5e1" rx="1" />
      <rect x="160" y="97" width="6" height="6" fill="#cbd5e1" rx="1" />
    </>
  ),
  rare: (
    <>
      <circle cx="100" cy="100" r="58" fill="none" stroke="url(#afCobalt)" strokeWidth="10" />
      <circle className="af-energy" cx="100" cy="100" r="58" fill="none" stroke="#e0f2fe" strokeWidth="2" opacity="0.9" />
      <path d="M 100,24 L 112,38 L 88,38 Z" fill="url(#afCobalt)" stroke="#38bdf8" strokeWidth="1" />
      <path d="M 100,176 L 112,162 L 88,162 Z" fill="url(#afCobalt)" stroke="#38bdf8" strokeWidth="1" />
      <path d="M 24,100 L 38,112 L 38,88 Z" fill="url(#afCobalt)" stroke="#38bdf8" strokeWidth="1" />
      <path d="M 176,100 L 162,112 L 162,88 Z" fill="url(#afCobalt)" stroke="#38bdf8" strokeWidth="1" />
    </>
  ),
  epic: (
    <>
      <circle cx="100" cy="100" r="58" fill="none" stroke="url(#afPurple)" strokeWidth="12" />
      <circle className="af-energy" cx="100" cy="100" r="58" fill="none" stroke="#f0abfc" strokeWidth="2.5" opacity="0.9" />
      <polygon points="100,16 112,34 88,34" fill="url(#afPurple)" stroke="#e879f9" strokeWidth="1" />
      <polygon points="100,184 112,166 88,166" fill="url(#afPurple)" stroke="#e879f9" strokeWidth="1" />
      <path d="M 38,38 L 56,38 L 38,56 Z" fill="url(#afPurple)" stroke="#e879f9" strokeWidth="1" />
      <path d="M 162,38 L 144,38 L 162,56 Z" fill="url(#afPurple)" stroke="#e879f9" strokeWidth="1" />
      <path d="M 38,162 L 56,162 L 38,144 Z" fill="url(#afPurple)" stroke="#e879f9" strokeWidth="1" />
      <path d="M 162,162 L 144,162 L 162,144 Z" fill="url(#afPurple)" stroke="#e879f9" strokeWidth="1" />
    </>
  ),
  legendary: (
    <>
      <g className="af-spin-cw" style={{ transformOrigin: 'center' }}>
        <circle cx="100" cy="100" r="78" fill="none" stroke="url(#afGold)" strokeWidth="1.5" strokeDasharray="6 12 18 12" opacity="0.6" />
        <circle cx="100" cy="100" r="72" fill="none" stroke="#fde047" strokeWidth="1" strokeDasharray="4 20" opacity="0.8" />
      </g>
      <circle cx="100" cy="100" r="60" fill="none" stroke="url(#afGold)" strokeWidth="14" />
      <circle className="af-spin-ccw" style={{ transformOrigin: 'center' }} cx="100" cy="100" r="60" fill="none" stroke="#fef08a" strokeWidth="2" strokeDasharray="25 15 5 15" />
      <path d="M 100,12 L 112,32 L 124,20 L 130,38 L 100,34 L 70,38 L 76,20 L 88,32 Z" fill="url(#afGold)" stroke="#fef08a" strokeWidth="1.5" />
      <polygon points="100,18 107,28 100,36 93,28" fill="#b91c1c" stroke="#fef08a" strokeWidth="1" />
      <path d="M 54,162 L 72,185 L 100,193 L 128,185 L 146,162 L 100,172 Z" fill="url(#afGold)" stroke="#fef08a" strokeWidth="1.5" />
    </>
  ),
  mythic: (
    <>
      <g className="af-spin-cw" style={{ transformOrigin: 'center' }}>
        <circle cx="100" cy="100" r="85" fill="none" stroke="url(#afVoidPurple)" strokeWidth="2" strokeDasharray="20 35 10 25" />
        <polygon points="100,10 105,18 95,18" fill="#c084fc" />
        <polygon points="100,190 105,182 95,182" fill="#c084fc" />
      </g>
      <g className="af-spin-ccw" style={{ transformOrigin: 'center' }}>
        <circle cx="100" cy="100" r="74" fill="none" stroke="url(#afCrimson)" strokeWidth="2.5" strokeDasharray="12 18 30 15" />
        <circle cx="100" cy="100" r="58" fill="none" stroke="#ef4444" strokeWidth="2" />
      </g>
      <polygon points="100,16 114,38 86,38" fill="url(#afObsidian)" stroke="#ef4444" strokeWidth="1.5" />
      <polygon points="100,184 114,162 86,162" fill="url(#afObsidian)" stroke="#ef4444" strokeWidth="1.5" />
      <polygon points="16,100 38,114 38,86" fill="url(#afObsidian)" stroke="#ef4444" strokeWidth="1.5" />
      <polygon points="184,100 162,114 162,86" fill="url(#afObsidian)" stroke="#ef4444" strokeWidth="1.5" />
      <polygon points="35,35 58,42 42,58" fill="url(#afCrimson)" stroke="#fca5a5" strokeWidth="1" />
      <polygon points="165,35 142,42 158,58" fill="url(#afCrimson)" stroke="#fca5a5" strokeWidth="1" />
      <polygon points="35,165 58,158 42,142" fill="url(#afCrimson)" stroke="#fca5a5" strokeWidth="1" />
      <polygon points="165,165 142,158 158,142" fill="url(#afCrimson)" stroke="#fca5a5" strokeWidth="1" />
      <circle cx="100" cy="30" r="3.5" fill="#ef4444" />
      <circle cx="100" cy="170" r="3.5" fill="#ef4444" />
    </>
  ),
};

const centerAbs: React.CSSProperties = {
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
};

interface Props {
  rarity: FrameRarity;
  /** đường kính avatar (px) — khung tự scale gấp đôi quanh avatar */
  sizePx: number;
  /** bật hạt (canvas) — chỉ nên bật cho avatar lớn để nhẹ máy */
  withParticles?: boolean;
}

const AvatarFrame = ({ rarity, sizePx, withParticles }: Props) => {
  const cfg = CFG[rarity];
  const frame = sizePx * 1.6;
  const imgFrame = FRAME_IMG[rarity];
  // Khung ảnh: tỉ lệ khung/avatar theo từng bậc (mặc định 1.525 = AVAcab).
  const imgSize = sizePx * (FRAME_MULT[rarity] ?? 1.525);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const doParticles = !imgFrame && !!withParticles && sizePx >= 56 && !prefersReducedMotion();

  // Hạt bụi xoay quanh viền (Cổ Điển/Hiếm/Sử Thi/Thần Thoại) — nhiều màu, 3 tầng, chậm rãi.
  const palette = VORTEX_PALETTE[rarity];
  const doVortex = !!palette && !!withParticles && sizePx >= 56 && !prefersReducedMotion();
  const vortex = useMemo(() => {
    if (!palette) return [];
    const n = Math.round(70 * Math.min(1, sizePx / 160)); // avatar lớn 70 hạt, nhỏ ít hơn cho nhẹ
    const r = () => Math.random();
    return Array.from({ length: n }, () => {
      const lt = r();
      // 3 tầng bán kính (146-169 trong khung 360) → theo tỉ lệ imgSize
      const rTest = lt < 0.33 ? r() * 4 + 146 : lt < 0.66 ? r() * 4 + 156 : r() * 5 + 164;
      const color = palette[Math.floor(r() * palette.length)];
      return {
        size: +(r() * 2 + 1).toFixed(1),
        color,
        glow: +(r() * 5 + 3).toFixed(1),
        startAngle: Math.round(r() * 360),
        radius: +((rTest / 360) * imgSize).toFixed(1),
        duration: +(r() * 3.5 + 7).toFixed(2),
        delay: +(r() * -10).toFixed(2),
        maxOpacity: +(r() * 0.3 + 0.7).toFixed(2),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rarity, sizePx, imgSize]);

  useEffect(() => {
    if (!doParticles) return;
    const cv = canvasRef.current;
    const wrap = wrapRef.current;
    if (!cv || !wrap) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) registerParticles(cv, cfg.particle, cfg.color, cfg.density);
          else unregisterParticles(cv);
        }
      },
      { threshold: 0 }
    );
    io.observe(wrap);
    return () => {
      io.disconnect();
      unregisterParticles(cv);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doParticles, rarity]);

  return (
    <>
      {/* Quầng sáng — nằm SAU avatar (z thấp) */}
      <span
        aria-hidden
        className={cfg.fastPulse ? 'af-core-pulse-fast' : 'af-core-pulse'}
        style={{
          ...centerAbs,
          width: frame * 0.62,
          height: frame * 0.62,
          borderRadius: '50%',
          background: cfg.glow,
          filter: `blur(${Math.max(5, frame * 0.1)}px)`,
          opacity: 0.6,
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />
      {/* Khung + hạt + vệt sáng — nằm TRƯỚC avatar */}
      <div
        ref={wrapRef}
        aria-hidden
        style={{ ...centerAbs, width: frame, height: frame, zIndex: 20, pointerEvents: 'none' }}
      >
        {imgFrame ? (
          <>
            <img
              src={imgFrame}
              alt=""
              style={{
                ...centerAbs,
                width: imgSize,
                height: imgSize,
                objectFit: 'contain',
                filter: `drop-shadow(0 0 ${imgSize * 0.07}px rgba(56,189,248,0.6)) drop-shadow(0 0 ${
                  imgSize * 0.12
                }px rgba(186,85,211,0.45)) drop-shadow(0 ${imgSize * 0.03}px ${
                  imgSize * 0.06
                }px rgba(0,0,0,0.75))`,
              }}
            />
            {doVortex &&
              vortex.map((p, i) => (
                <span
                  key={i}
                  className="af-dust"
                  style={{
                    width: p.size,
                    height: p.size,
                    background: p.color,
                    boxShadow: `0 0 ${p.glow}px ${p.color}`,
                    animationDuration: `${p.duration}s`,
                    animationDelay: `${p.delay}s`,
                    ['--start-angle' as any]: `${p.startAngle}deg`,
                    ['--orbit-radius' as any]: `${p.radius}px`,
                    ['--max-opacity' as any]: p.maxOpacity,
                  }}
                />
              ))}
            {/* Huyền Thoại: 4 hạt sáng nhẹ bay lên trong avatar (theo avalegend) */}
            {rarity === 'legendary' && !!withParticles && sizePx >= 56 && !prefersReducedMotion() && (
              <span className="af-firefly-box" style={{ ...centerAbs, width: sizePx, height: sizePx }}>
                {FIREFLIES.map((f, i) => (
                  <span
                    key={i}
                    className="af-firefly"
                    style={{
                      left: `${f.l}%`,
                      bottom: `${f.b}%`,
                      width: f.s,
                      height: f.s,
                      background: f.c,
                      animationDuration: `${f.d}s`,
                      animationDelay: `${f.dl}s`,
                      ['--rise' as any]: `${Math.round(sizePx * 0.55)}px`,
                    }}
                  />
                ))}
              </span>
            )}
          </>
        ) : (
          <>
            {doParticles && (
              <canvas
                ref={canvasRef}
                style={{ ...centerAbs, width: frame * 1.2, height: frame * 1.2 }}
              />
            )}
            <svg
              viewBox="0 0 200 200"
              style={{
                ...centerAbs,
                width: frame,
                height: frame,
                filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.6))',
              }}
            >
              {FRAME[rarity]}
            </svg>
          </>
        )}
        <span
          className="af-shimmer"
          style={{ ...centerAbs, width: sizePx, height: sizePx, borderRadius: '50%' }}
        />
      </div>
    </>
  );
};

export default AvatarFrame;
